import { useVirtualizer } from "@tanstack/react-virtual";
import { AlertCircle, CheckIcon, ChevronDown, Loader2, X } from "lucide-react";
import { useState, useRef } from "react";
import { useSpinDelay } from "spin-delay";
import { PopoverTriggerButton } from "../PopoverTriggerButton";
import { type ComboboxProps, type ComboboxOption, type SingleProps, type MultiProps } from "./types";
import { Button } from "@/components/ui/button";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from "@/components/ui/command";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils/common";
import { getErrorMessage } from "@/lib/utils/errors";

export function Combobox<T extends string>({
  className,
  options,
  virtualisationThreshold = 100,
  inputRef,
  ...props
}: ComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const shouldVirtualize = options.length > virtualisationThreshold;

  return (
    <ComboboxRoot {...props} inputRef={inputRef} className={className} options={options} open={open} setOpen={setOpen}>
      {shouldVirtualize ? (
        <VirtualisedOptionsList {...props} options={options} setOpen={setOpen} />
      ) : (
        <OptionsList {...props} options={options} setOpen={setOpen} />
      )}
    </ComboboxRoot>
  );
}

type OptionsListProps<T extends string> = {
  options: ComboboxOption<T>[];
  emptyText?: string;
  handleClear?: () => void;
  setOpen: (open: boolean) => void;
} & (SingleProps<T> | MultiProps<T>);

function OptionsList<T extends string>(props: OptionsListProps<T>) {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredOptions = props.options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleSelect = (value: T) => {
    if (props.type === "multi") {
      const newSelected = props.selected.includes(value)
        ? props.selected.filter((item) => item !== value)
        : [...props.selected, value];
      props.onChange(newSelected);
    } else {
      props.onChange(props.selected === value ? null : value);
      props.setOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (props.type === "multi") {
      const filteredValues = filteredOptions.map((opt) => opt.value);
      const newSelected = [...new Set([...props.selected, ...filteredValues])];
      props.onChange(newSelected);
    }
  };

  const isSelected = (value: T) => (props.type === "multi" ? props.selected.includes(value) : props.selected === value);

  const showSelectAllButton = props.type === "multi" && searchQuery.length > 0 && filteredOptions.length > 0;

  return (
    <Command>
      <CommandInput placeholder="Search..." onValueChange={setSearchQuery} />
      {showSelectAllButton && (
        <SelectAllButton
          filteredOptions={filteredOptions}
          selected={props.selected}
          handleSelectAll={handleSelectAll}
        />
      )}
      <CommandList className="max-h-[200px] overflow-auto">
        <ContentFallback emptyText={props.emptyText}>
          <CommandGroup>
            {filteredOptions.map((option) => (
              <CommandItem
                key={option.value}
                onSelect={() => handleSelect(option.value)}
                className="relative cursor-default pl-8"
              >
                <span className="absolute left-2">{isSelected(option.value) && <CheckIcon className="h-4 w-4" />}</span>
                {option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </ContentFallback>
      </CommandList>
    </Command>
  );
}

type VirtualisedOptionsListProps<T extends string> = {
  options: ComboboxOption<T>[];
  emptyText?: string;
  setOpen: (open: boolean) => void;
} & (SingleProps<T> | MultiProps<T>);

function VirtualisedOptionsList<T extends string>(props: VirtualisedOptionsListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const filteredOptions = props.options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 5,
  });

  const handleSelect = (value: T) => {
    if (props.type === "multi") {
      const newSelected = props.selected.includes(value)
        ? props.selected.filter((item) => item !== value)
        : [...props.selected, value];
      props.onChange(newSelected);
    } else {
      props.onChange(props.selected === value ? null : value);
      props.setOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (props.type === "multi") {
      const filteredValues = filteredOptions.map((opt) => opt.value);
      const newSelected = [...new Set([...props.selected, ...filteredValues])];
      props.onChange(newSelected);
    }
  };

  const isSelected = (value: T) => (props.type === "multi" ? props.selected.includes(value) : props.selected === value);

  const items = virtualizer.getVirtualItems();
  const showSelectAllButton = props.type === "multi" && searchQuery.length > 0 && filteredOptions.length > 0;

  return (
    <Command>
      <CommandInput placeholder="Search..." onValueChange={setSearchQuery} />
      {showSelectAllButton && (
        <SelectAllButton
          filteredOptions={filteredOptions}
          selected={props.selected}
          handleSelectAll={handleSelectAll}
        />
      )}
      <CommandList ref={parentRef} className="max-h-[200px] overflow-auto">
        <ContentFallback emptyText={props.emptyText}>
          <CommandGroup>
            <div
              className="w-full relative"
              style={{
                height: virtualizer.getTotalSize(),
              }}
            >
              <div
                className="absolute top-0 left-0 w-full"
                style={{
                  transform: `translateY(${items[0]?.start ?? 0}px)`,
                }}
              >
                {items.map((virtualItem) => {
                  const item = filteredOptions[virtualItem.index];
                  if (!item) return null;
                  return (
                    <CommandItem
                      key={virtualItem.key}
                      data-index={virtualItem.index}
                      ref={virtualizer.measureElement}
                      onSelect={() => handleSelect(item.value)}
                      value={item.label}
                      className="relative cursor-default pl-8 whitespace-pre-line"
                    >
                      <span className="absolute left-2">
                        {isSelected(item.value) && <CheckIcon className="h-4 w-4" />}
                      </span>
                      {item.label}
                    </CommandItem>
                  );
                })}
              </div>
            </div>
          </CommandGroup>
        </ContentFallback>
      </CommandList>
    </Command>
  );
}

type ComboboxRootProps<T extends string> = ComboboxProps<T> & {
  open: boolean;
  setOpen: (open: boolean) => void;
  children: React.ReactNode;
};

export function ComboboxRoot<T extends string>({
  id,
  options,
  selected,
  name,
  open,
  setOpen,
  placeholder = "Select",
  disabled = false,
  type,
  onChange,
  children,
  className,
  isModal,
  inputRef,
  error,
  loading,
}: ComboboxRootProps<T>) {
  const selectedItems =
    type === "multi"
      ? selected.map((value) => options.find((opt) => opt.value === value)?.label).filter(Boolean)
      : selected
        ? [options.find((opt) => opt.value === selected)?.label].filter(Boolean)
        : [];

  const triggerText =
    type === "multi"
      ? selectedItems.length === 0
        ? placeholder
        : selectedItems.length <= 2
          ? selectedItems.join(", ")
          : `${selected.length} selected`
      : selected
        ? (options.find((opt) => opt.value === selected)?.label ?? placeholder)
        : placeholder;

  const isOptionSelected = type === "multi" ? selected.length === 0 : !selected;

  const showLoadingState = useSpinDelay(loading || false, { minDuration: 500 });

  const handleClear = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (type === "multi") {
      onChange([]);
    } else {
      onChange(null);
    }
  };

  const showClearButton = type === "multi" && selected.length > 0;

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen} modal={isModal}>
        <PopoverTrigger asChild>
          <PopoverTriggerButton
            id={id}
            role="combobox"
            name={name}
            aria-expanded={open}
            disabled={disabled || !!error}
            className={cn(!!error ? "border-red-500" : "", className)}
            ref={inputRef}
          >
            <span
              className={cn("truncate font-normal", (isOptionSelected || showLoadingState) && "text-muted-foreground")}
            >
              {!!error ? (
                <div className="flex items-center justify-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-foreground">{getErrorMessage(error)}</span>
                </div>
              ) : showLoadingState ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading...</span>
                </div>
              ) : (
                triggerText
              )}
            </span>
            <div className="ml-2 flex items-center gap-2">
              {showClearButton && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Clear selection"
                  className="rounded-full hover:bg-accent cursor-pointer"
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleClear(e);
                    }
                  }}
                >
                  <X className="size-4 opacity-50 hover:opacity-100" />
                </span>
              )}
              <ChevronDown
                className={cn("h-4 w-4 shrink-0", (isOptionSelected || showLoadingState) && "text-muted-foreground")}
              />
            </div>
          </PopoverTriggerButton>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0">{children}</PopoverContent>
      </Popover>
    </div>
  );
}

export function ContentFallback({
  children,
  emptyText = "No results found.",
}: {
  emptyText?: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <CommandEmpty>{emptyText}</CommandEmpty>
      {children}
    </>
  );
}

export function SelectAllButton<T extends string>({
  filteredOptions,
  selected,
  handleSelectAll,
}: {
  filteredOptions: ComboboxOption<T>[];
  selected: T[];
  handleSelectAll: () => void;
}) {
  const allFilteredSelected =
    filteredOptions.length > 0 && filteredOptions.every((opt) => selected.includes(opt.value));
  const isDisabled = filteredOptions.length === 0 || allFilteredSelected;

  return (
    <>
      <Button
        data-testid="select-all-button"
        type="button"
        variant={"ghost"}
        size={"sm"}
        disabled={isDisabled}
        className="m-1 text-left hover:bg-transparent"
        onClick={handleSelectAll}
      >
        Select All
      </Button>
      <hr className="-mx-1 h-px bg-gray-200" />
    </>
  );
}
